import React, { useState } from 'react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import quarterOfYear from 'dayjs/plugin/quarterOfYear'
import Select from '../../molecules/Select'
import { useGetBlueprintTimeseriesRange } from '../../../api/denali'
import { timeseriesLatchPropType } from './propTypes'
import PerPeriodControls from './PerPeriodControls'

dayjs.extend(utc)
dayjs.extend(quarterOfYear)

/**
 * Pure helper: renders a period bucket (a `periodStart` date string) as a
 * human-readable label appropriate for the period granularity. Returns
 * `null` for `day` (the daily UI uses raw date pickers, not a bucket list).
 *
 * @visibleForTesting
 */
export function formatBucketLabel (periodStart, period) {
  if (!periodStart) return null
  const d = dayjs.utc(periodStart)
  if (!d.isValid()) return String(periodStart)
  switch (period) {
    case 'year': return d.format('YYYY')
    case 'quarter': return `Q${d.quarter()} ${d.format('YYYY')}`
    case 'month': return d.format('MMM YYYY')
    case 'week': return `Week of ${d.format('MMM D, YYYY')}`
    case 'day': return null
    default: return d.format('YYYY-MM-DD')
  }
}

/**
 * Pure helper: resolves the currently-effective bucket value (a `periodStart`
 * date string) given the picker's state and the metadata it has on hand.
 * Returns `null` when no bucket should be active — in particular when the
 * `rangeMeta` payload doesn't match the currently-selected `period` (which
 * happens transiently during a period switch because the React Query hook
 * uses `keepPreviousData: true`).
 *
 * @visibleForTesting
 */
export function selectEffectiveBucket ({ period, selectedBucket, rangeMeta }) {
  if (!period || period === 'day') return null
  if (!rangeMeta || rangeMeta.period !== period) return null
  return selectedBucket ?? rangeMeta?.buckets?.[0] ?? null
}

/**
 * Pure helper: turns a single `periodStart` bucket into an absolute
 * `{from, to}` range covering the entire period. The end-of-period date is
 * derived via `dayjs.endOf(period)`.
 *
 * @visibleForTesting
 */
export function bucketToRange (periodStart, period) {
  if (!periodStart) return null
  const d = dayjs.utc(periodStart)
  if (!d.isValid()) return null
  const unit = period === 'day' ? 'day' : period
  return {
    from: d.format('YYYY-MM-DD'),
    to: d.endOf(unit).format('YYYY-MM-DD')
  }
}

const PERIOD_OPTIONS = [
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'quarter', label: 'Quarter' },
  { value: 'year', label: 'Year' }
]

/**
 * Display-only mapping from a period enum to its capitalised label. Exported
 * for reuse by `PerPeriodControls` (empty-state placeholder text).
 */
export function labelForPeriod (period) {
  return PERIOD_OPTIONS.find(p => p.value === period)?.label || period
}

/**
 * Picker UI for a blueprint purpose's `timeseriesLatch`. Owns only the
 * user's period selection; everything else is derived during render or
 * delegated to a `<PerPeriodControls key={period}>` subtree that owns the
 * transient per-period state (selected bucket, daily from/to dates).
 *
 * The `key={period}` remount on period change is the React-idiomatic
 * replacement for the various "reset state on period change" / "seed from
 * bounds when they arrive" effects that the picker used to manage manually.
 *
 * External contract:
 *   - Calls `onChange(period, { from, to })` whenever the resolved selection
 *     changes (handled by `PerPeriodControls`).
 *   - Renders a period select when `supportedPeriods.length > 1`, otherwise
 *     a static subtle label. Per the snapshot latch picker style, no labels
 *     above controls.
 */
function BlueprintTimeseriesLatchPicker ({ blueprintCode, purposeCode, timeseriesLatch, onChange }) {
  const [selectedPeriod, setSelectedPeriod] = useState(timeseriesLatch?.defaultPeriod ?? null)

  const { data: rangeMeta } = useGetBlueprintTimeseriesRange(
    blueprintCode,
    purposeCode,
    { period: selectedPeriod, enabled: !!timeseriesLatch }
  )

  const supportedPeriods = rangeMeta?.supportedPeriods?.length
    ? rangeMeta.supportedPeriods
    : (timeseriesLatch?.supportedPeriods || [])

  const defaultPeriod = rangeMeta?.defaultPeriod ??
    timeseriesLatch?.defaultPeriod ??
    supportedPeriods[0] ??
    null

  const period = supportedPeriods.includes(selectedPeriod) ? selectedPeriod : defaultPeriod
  const dataMatchesPeriod = !!period && rangeMeta?.period === period
  const bounds = dataMatchesPeriod ? (rangeMeta?.bounds ?? null) : null
  const buckets = dataMatchesPeriod ? (rangeMeta?.buckets ?? null) : null

  if (!timeseriesLatch || !period) return null

  const periodOptions = supportedPeriods.map(p => ({ value: p, label: labelForPeriod(p) }))
  const showPeriodSelect = supportedPeriods.length > 1

  return (
    <>
      {showPeriodSelect
        ? (
          <Select
            value={period}
            onChange={setSelectedPeriod}
            options={periodOptions}
          />
        )
        : (
          <span className='font-size-sm' style={{ color: 'var(--text-color-subtle)' }}>
            {labelForPeriod(period)}
          </span>
        )}

      <PerPeriodControls
        key={period}
        period={period}
        bounds={bounds}
        buckets={buckets}
        onChange={onChange}
      />
    </>
  )
}

BlueprintTimeseriesLatchPicker.propTypes = {
  blueprintCode: PropTypes.string,
  purposeCode: PropTypes.string,
  timeseriesLatch: timeseriesLatchPropType,
  onChange: PropTypes.func.isRequired
}

export default BlueprintTimeseriesLatchPicker
